import { useEffect, useMemo, useState } from "react";
import { colors } from "@/constant/colors";
import Image from "@/components/Image";
import { getTotalDiamondByElementType } from "@/constant/reward";
import { LuBug } from "react-icons/lu";
import DiamondFloat, { DiamondFloatRef } from "../Animation/diamondFloatAnimation";
import PracticeCard from "./practice-card";
import { AnswerRecord, ElementType, ModeType, PracticeQuestion } from "@/constant/schema";
import ReviewAnswer from "./review-answer";
import ReportPopup from "../LearnCard/ReportPopup";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useReward } from "@/constant/reward";
import useRewardStore from "@/store/useRewardStore";
import NormalButton from "../Button/normal";
import LiveButton from "../Button/LiveButton";
import report, { ReportEvent } from "@/lib/report";
import LearnLiveTutorModal from "../LearnCard/live-tutor-modal";
import { buildPracticeQuestionLiveTutorProblem } from "../LearnCard/live-tutor-selection";
import type { PublicStudyAuthPromptReason } from "@/components/public-study/PublicStudyAuthPrompt";
import { useChatStore } from "@/store/useChatStore";

interface PracticeNodeProps {
  // Display state
  currentQuestion: PracticeQuestion | undefined;
  currentAnswer: { selectedOption: string | null; isCorrect: boolean | null };
  showSummary: boolean;
  reviewAnswer: boolean;
  showReportPopup: boolean;
  wasFirstAnswerWrong: boolean;
  showRetryLiveTutor: boolean;

  // Question data
  quizQuestions: PracticeQuestion[];
  answerRecords: AnswerRecord[];

  // Progress
  allFinished: boolean;

  // Identifiers
  questId: string;
  chapterId: string;
  lessonId?: string;
  lessonTitle: string;
  modeType: ModeType;
  avatarUrl: string;
  subject?: string | null;

  // Handlers
  handleAnswer: (option: string, isCorrect: boolean, autoSubmit?: boolean) => void;
  handleReviewAnswers: () => void;
  handleReviewClose: () => void;
  setShowReportPopup: (show: boolean) => void;
  setShowAllChapters: (show: boolean) => void;
  onInputChange?: (value: string) => void;
  triggerCheck?: boolean;

  // Refs
  currentDiamondImageRef: React.RefObject<HTMLImageElement>;
  diamondFloatRef: React.RefObject<DiamondFloatRef | null>;

  // Chat state
  isChatActive?: boolean;

  comboCount: number;
  canReview: boolean;
  reviewMode: boolean;
  setShowReviewModal: (show: boolean) => void;
  onRequireAuth?: (reason: PublicStudyAuthPromptReason) => boolean;
}

const PracticeNode = ({
  currentQuestion,
  currentAnswer,
  showSummary,
  reviewAnswer,
  showReportPopup,
  wasFirstAnswerWrong,
  showRetryLiveTutor,
  quizQuestions,
  answerRecords,
  allFinished,
  questId,
  chapterId,
  lessonId,
  lessonTitle,
  modeType,
  avatarUrl,
  subject,
  handleAnswer,
  handleReviewAnswers,
  handleReviewClose,
  setShowReportPopup,
  setShowAllChapters,
  onInputChange,
  triggerCheck,
  currentDiamondImageRef,
  diamondFloatRef,
  isChatActive = false,
  comboCount,
  canReview,
  setShowReviewModal,
  onRequireAuth,
}: PracticeNodeProps) => {
  const reward = useReward();
  const [isLiveTutorOpen, setIsLiveTutorOpen] = useState(false);
  const currentQuestionId = currentQuestion?.id ?? '';
  const currentDisplayState = useChatStore((state) =>
    currentQuestionId ? state.element_display_states[currentQuestionId] : undefined,
  );
  const displayedOptions = useMemo(
    () => (
      Array.isArray(currentDisplayState?.shuffledOptions)
        ? currentDisplayState.shuffledOptions
            .filter((item: unknown): item is string => typeof item === 'string' && item.trim().length > 0)
            .map((item: string) => item.trim())
        : undefined
    ),
    [currentDisplayState],
  );

  const liveTutorProblem = useMemo(
    () => (
      showRetryLiveTutor
        ? buildPracticeQuestionLiveTutorProblem(currentQuestion ?? null, subject, displayedOptions)
        : null
    ),
    [currentQuestion, displayedOptions, showRetryLiveTutor, subject],
  );

  useEffect(() => {
    setIsLiveTutorOpen(false);
  }, [currentQuestion?.id]);

  const getDiamondCount = () => {
    return getTotalDiamondByElementType(ElementType.QUESTION, modeType === ModeType.CHALLENGE);
  }

  const multiplier = comboCount >= 7 ? 4 : comboCount >= 5 ? 3 : comboCount >= 3 ? 2 : 1;

  // Show summary page after completing all questions
  if (showSummary && allFinished) {
    const totalQuestions = quizQuestions.length;
    const firstCorrectCount = answerRecords.filter(record =>
      record.firstIsCorrect === undefined ? record.isCorrect : record.firstIsCorrect
    ).length;

    return (
      <div
        className="flex w-full max-w-3xl flex-col items-center justify-center px-4 py-6 text-center sm:px-6"
        style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}
      >
        <div className="flex w-full flex-col items-center justify-center gap-4 sm:flex-row sm:items-end sm:gap-6">
          <div className="text-center">
            <h2 className="emphasize-text mb-3 text-2xl font-bold sm:mb-4 sm:text-3xl" style={{ color: colors.mainTextColor }}>
              Your Score
            </h2>
            <div className="mb-2 text-5xl font-bold sm:mb-4 sm:text-6xl">
              <span style={{ color: colors.wrongAnswerColor }}>{firstCorrectCount}</span>
              <span style={{ color: colors.wrongAnswerColor }}>/{totalQuestions}</span>
            </div>
          </div>
          <Image
            src="/assets/practice_finish.png"
            alt="practice_finish"
            width={100}
            height={100}
            className="h-32 w-32 object-contain sm:h-[200px] sm:w-[200px]"
            optimizedWidth={500}
          />
        </div>

        <span className="display-text-bold mt-4 max-w-lg text-sm font-semibold text-[#333333] sm:text-base">
          Congratulations! You have mastered this practice.
        </span>

        <div className="mt-8 flex w-full max-w-xl flex-col gap-3 sm:flex-row sm:gap-4">
          <NormalButton
            colorType="gray"
            className="w-full sm:flex-1"
            textClassName="text-lg sm:text-xl"
            onClick={handleReviewAnswers}
          >
            Review Answers
          </NormalButton>
          <NormalButton
            onClick={() => {
              report(ReportEvent.PRACTICE_REVIEW_MODAL_OPEN, {
                quest_id: questId,
                chapter_id: chapterId,
                lesson_id: lessonId,
              });
              setShowReviewModal(true);
            }}
            colorType="gray"
            className="w-full sm:flex-1"
            textClassName="text-lg sm:text-xl"
          >
            Redo This Practice
          </NormalButton>  
        </div>
        <div className="mt-3 w-full max-w-xl">
          {modeType !== ModeType.MISTAKE && (
            <NormalButton
              className="w-full"
              textClassName="text-lg sm:text-xl"
              onClick={() => setShowAllChapters(true)}
            >
              Continue
            </NormalButton>
          )}
        </div>
      </div>
    );
  }

  if (reviewAnswer) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-8">
        <ReviewAnswer
          answerRecords={answerRecords}
          allQuestions={quizQuestions}
          onClose={handleReviewClose}
        />
      </div>
    );
  }

  return (
    <>
      <div
        className="relative flex flex-col gap-2 pt-4 justify-center bg-white w-full md:w-3/4 border-1 border-gray-300 rounded-xl p-4"
        onClick={() => reward.heart <= 0 && useRewardStore.setState({ buyHeartPopup: true })}
      >
        <div className="flex flex-row w-full mb-5 items-center justify-between px-4">
          <div className="flex flex-row items-center gap-2">
            {
              avatarUrl && (
                <Image 
                  src={avatarUrl}
                  alt="Assistant Avatar" 
                  className="rounded-full object-contain" 
                  width={32} 
                  height={32} 
                  optimizedWidth={80}
                />
              )
            }
            <div className="text-xl emphasize-text" style={{ color: colors.mainTitleColor }}>Pengi</div>
          </div>
          <div className="flex flex-row items-center gap-2">
            {
              !canReview && (
                <div className={`flex text-xs bg-[#F2ECE1] rounded-full px-2 py-0 w-fit items-center gap-2`}>
                  <div className="w-4">
                    <Image src={'/assets/diamond-3.png'} ref={currentDiamondImageRef} alt={'diamond'} width={14} height={14} optimizedWidth={50} />
                  </div>
                  <span className="text-base emphasize-text">{multiplier * getDiamondCount()}</span>
                </div>  
              )
            }
            {/* Bug report button */}
            <button
              onClick={() => {
                report(ReportEvent.USER_REPORT_BUTTON, {});
                setShowReportPopup(true);
              }}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors duration-200 flex flex-row items-center gap-2"
              aria-label="Report a problem"
            >
              <span className="text-base font-medium" style={{ color: colors.mainTitleColor }}>Report a Bug</span>
              <LuBug className="w-5 h-5" style={{ color: colors.mainTitleColor }} />
            </button>
          </div>
        </div>

        {wasFirstAnswerWrong && (
          <div className="display-text-bold text-yellow-600 font-semibold flex flex-row items-center gap-2 ml-8">
            <ExclamationTriangleIcon className="w-5 h-5" />
              Previously answered incorrectly.
          </div>
        )}

        <PracticeCard
          key={currentQuestion?.id}
          question={currentQuestion!}
          disabled={reward.heart <= 0}
          handleAnswer={handleAnswer}
          currentAnswer={currentAnswer}
          allFinished={allFinished}
          onInputChange={onInputChange}
          triggerCheck={triggerCheck}
          isChatActive={isChatActive}
        />
        {showRetryLiveTutor && liveTutorProblem && (
          <div className="mt-4 flex w-full justify-center">
            <LiveButton
              ariaLabel="Open live tutor"
              label="Live"
              onClick={(e) => {
                e.stopPropagation();
                if (onRequireAuth?.('live')) {
                  return;
                }
                setIsLiveTutorOpen(true);
              }}
            />
          </div>
        )}

        <div className="h-6" />
      </div>

      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 flex items-center justify-center w-96 h-96 pointer-events-none">
        <DiamondFloat
          ref={diamondFloatRef}
          exp={getDiamondCount() * multiplier}
          color={colors.mainTextColor}
        />
      </div>

      {/* Report Popup */}
      <ReportPopup
        isOpen={showReportPopup}
        onClose={() => setShowReportPopup(false)}
        elementTitle={modeType === ModeType.CHALLENGE ? "Challenge Session" : "Practice Session"}
        lessonTitle={lessonTitle || (modeType === ModeType.CHALLENGE ? "Math Challenge Mode" : "Math Practice Mode")}
        specificElement={{
          id: null,
          title: `Math ${modeType === ModeType.CHALLENGE ? 'Challenge' : 'Practice'} Session`
        }}
        reportType={modeType === ModeType.CHALLENGE ? "challenge" : "practice"}
        questionId={currentQuestion?.id}
        contextQuestId={questId}
        contextChapterId={chapterId}
        contextLessonId={lessonId}
      />

      {showRetryLiveTutor && liveTutorProblem && (
        <LearnLiveTutorModal
          problem={liveTutorProblem}
          isOpen={isLiveTutorOpen}
          onClose={() => setIsLiveTutorOpen(false)}
        />
      )}
    </>
  );
};

export default PracticeNode;
